import _ from "lodash";
import i18next from "i18next";
import { NEW } from "../../../i18n/keysTranslations";
import { ADS_KEYS_TEXT, ADS_OBJECT_TYPES } from "../../../utils/constants";
import { entityMatchesId, idsMatch } from "../../../utils/numbers";

export const EMPTY_VALUE = "-";

export const changeNeedsPreviousValue = (change) => {
  const operation = change?.operation;
  return (
    operation !== OPERATION_TYPES.ADD &&
    operation !== OPERATION_TYPES.CREATE &&
    operation !== OPERATION_TYPES.REMOVE
  );
};

export const withStoredPreviousValues = (changes, getValues) =>
  (Array.isArray(changes) ? changes : []).map((change) => {
    if (
      !changeNeedsPreviousValue(change) ||
      change.previousValue !== undefined
    ) {
      return change;
    }

    return {
      ...change,
      previousValue: getOptimizationChangeCurrentValue(change, getValues),
    };
  });

// New structure constants
export const OPERATION_TYPES = {
  ADD: "add",
  REMOVE: "remove",
  UPDATE: "update",
  CREATE: "create",
};

// Helper function to get current value from campaign data
export const getOptimizationChangeCurrentValue = (change, getValues) => {
  if (
    change.operation === OPERATION_TYPES.ADD ||
    change.operation === OPERATION_TYPES.CREATE
  ) {
    return i18next.t(NEW); // Adding new value, so show "NEW" instead of empty
  }

  if (change.operation === OPERATION_TYPES.REMOVE) {
    // Prefer itemsByValue if present
    if (Array.isArray(change.itemsByValue)) {
      if (change._isExpanded && typeof change._itemIndex === "number") {
        return change.itemsByValue[change._itemIndex] || EMPTY_VALUE;
      }
      return change.itemsByValue.join(", ");
    }
    // Handle itemsByID: map IDs to names if possible
    if (Array.isArray(change.itemsByID)) {
      const campaignData = getValues();

      // Locate the source array in form data for this field to resolve IDs to display names
      const resolveNameById = (id) => {
        // Build base reference to the array for the field
        let container;
        if (change.entity === ADS_OBJECT_TYPES.campaign) {
          container =
            campaignData?.platformsData?.[change.platform]?.[change.field];
        } else if (change.entity === ADS_OBJECT_TYPES.assetsGroup) {
          const groups = campaignData?.assetsGroups || [];
          const idx = groups.findIndex((g) =>
            entityMatchesId(g, change.assetsGroupID || change.adGroupID)
          );
          container = idx >= 0 ? groups[idx]?.[change.field] : undefined;
        } else if (change.entity === ADS_OBJECT_TYPES.adGroup) {
          const groups = campaignData?.adsGroups || [];
          const idx = groups.findIndex((g) =>
            entityMatchesId(g, change.adGroupID)
          );
          container = idx >= 0 ? groups[idx]?.[change.field] : undefined;
        } else if (change.entity === ADS_OBJECT_TYPES.ad) {
          const groups = campaignData?.adsGroups || [];
          const gIdx = groups.findIndex((g) =>
            entityMatchesId(g, change.adGroupID)
          );
          if (gIdx >= 0) {
            const ads = groups[gIdx]?.ads || [];
            const aIdx = ads.findIndex((ad) =>
              entityMatchesId(ad, change.adID)
            );
            container = aIdx >= 0 ? ads[aIdx]?.[change.field] : undefined;
          }
        }

        // If container is array of objects, try finding by id/idPlatform
        if (Array.isArray(container)) {
          const found = container.find(
            (it) => entityMatchesId(it, id) || idsMatch(it?.value, id)
          );
          // Prefer common name keys
          return (
            found?.name || found?.value || found?.label || found?.title || id
          );
        }
        return id;
      };

      if (change._isExpanded && typeof change._itemIndex === "number") {
        const id = change.itemsByID[change._itemIndex];
        return resolveNameById(id) || EMPTY_VALUE;
      }
      // Not expanded: join resolved names
      return change.itemsByID.map((id) => resolveNameById(id)).join(", ");
    }
  }

  // Try to get current value from form data
  try {
    const campaignData = getValues();

    // Build path dynamically based on entity type
    const pathSegments = [];

    if (change.entity === ADS_OBJECT_TYPES.campaign) {
      if (change.field === "status") {
        pathSegments.push("states");
        pathSegments.push(change.platform);
      } else {
        // For campaign entity, access platformsData root level
        pathSegments.push("platformsData");
        pathSegments.push(change.platform);
        pathSegments.push(change.field);
      }
    } else if (change.entity === ADS_OBJECT_TYPES.assetsGroup) {
      pathSegments.push("assetsGroups");
      // Find assetsGroup by ID (could be id or idPlatform)
      const assetsGroups = campaignData.assetsGroups || [];
      const groupIndex = assetsGroups.findIndex((group) =>
        entityMatchesId(group, change.assetsGroupID || change.adGroupID)
      );
      if (groupIndex >= 0) {
        pathSegments.push(groupIndex.toString());
        pathSegments.push(change.field);
      }
    } else if (change.entity === ADS_OBJECT_TYPES.adGroup) {
      pathSegments.push("adsGroups");
      // Find adGroup by ID (could be id or idPlatform)
      const adsGroups = campaignData.adsGroups || [];
      const groupIndex = adsGroups.findIndex((group) =>
        entityMatchesId(group, change.adGroupID)
      );
      if (groupIndex >= 0) {
        pathSegments.push(groupIndex.toString());
        pathSegments.push(change.field);
      }
    } else if (change.entity === ADS_OBJECT_TYPES.ad) {
      pathSegments.push("adsGroups");
      // Find adGroup by ID
      const adsGroups = campaignData.adsGroups || [];
      const groupIndex = adsGroups.findIndex((group) =>
        entityMatchesId(group, change.adGroupID)
      );
      if (groupIndex >= 0) {
        pathSegments.push(groupIndex.toString());
        pathSegments.push("ads");
        // Find ad by ID
        const ads = adsGroups[groupIndex].ads || [];
        const adIndex = ads.findIndex((ad) => entityMatchesId(ad, change.adID));
        if (adIndex >= 0) {
          pathSegments.push(adIndex.toString());
          pathSegments.push(change.field);
        }
      }
    }

    // Navigate to the value
    let currentValue = campaignData;
    for (const segment of pathSegments) {
      if (currentValue && typeof currentValue === "object") {
        currentValue = currentValue[segment];
      } else {
        currentValue = undefined;
        break;
      }
    }

    // Format the current value based on field type
    if (currentValue !== undefined && currentValue !== null) {
      // Handle text fields that are arrays of objects {value, id}
      if (ADS_KEYS_TEXT.includes(change.field) && Array.isArray(currentValue)) {
        const values = currentValue
          .map((item) => (typeof item === "object" ? item.value : item))
          .filter(Boolean);
        // If this is an expanded row, return only the specific item
        if (change._isExpanded && typeof change._itemIndex === "number") {
          return values[change._itemIndex] || EMPTY_VALUE;
        }

        // If not expanded, return all values joined
        return values.length > 0 ? values : EMPTY_VALUE;
      }

      // Handle regular arrays
      if (Array.isArray(currentValue)) {
        // If this is an expanded row, return only the specific item
        if (change._isExpanded && typeof change._itemIndex === "number") {
          return currentValue[change._itemIndex] || EMPTY_VALUE;
        }

        // If not expanded, return all values joined
        return currentValue.length > 0 ? currentValue : EMPTY_VALUE;
      }

      if (typeof currentValue === "object") {
        return currentValue;
      }

      if (_.isArray(currentValue)) {
        return currentValue;
      }

      return String(currentValue);
    }
  } catch (error) {
    console.warn("Error getting current value:", error);
  }

  // Fallback placeholders based on field type
  if (ADS_KEYS_TEXT.includes(change.field)) {
    return "Current text content";
  }

  switch (change.field) {
    case "budget":
      return "";
    default:
      return "";
  }
};
